/* eslint-disable react/display-name */
import { useTheme } from '@material-ui/core'
import { LocalizedTypography } from 'components/Localization/LocalizedTypography'
import { ProjectStateAsString } from 'controllers/Project/ProjectStateMachine'
import { useMaterialTableLocalization } from 'customHooks/useMaterialTableLocalization'
import { ConnectionState } from 'features/SignalR/components/ConnectionState'
import { isEqual } from 'lodash'
import { matchSorter } from 'match-sorter'
import MaterialTable, { Column, Query, QueryResult } from 'material-table'
import { MoneyString } from 'model/Money'
import { forwardRef, useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useAppSelector } from 'store/configureStore'
import { useAppDispatch } from 'store/configureStore'
import {
  CreatorDto,
  ProjectSummaryDto,
  SalesPersonDto,
} from 'services/APIs/InternalAPI/internal-api.contracts'
import { ShowException } from 'store/Application/appActions'
import { RootAppState } from 'store/configureStore'
import { deleteProjects } from 'store/ProjectList/asyncActions/deleteProjects'
import { ReactComponent as SearchSvg } from '_icons/ui/search.svg'
import { ReactComponent as TrashSvg } from '_icons/ui/trash.svg'
import { ProjectsPageHelpers } from './ProjectsPageHelpers'
import { useProjectList } from './useProjectList'
import { useNavigate } from 'react-router-dom'

const columns: (t) => Column<ProjectSummaryDto>[] = (t) => [
  {
    title: t('project:order'),
    field: 'orderNumber',
    grouping: false,
    cellStyle: { textAlign: 'left', paddingRight: '40px' },
    headerStyle: {
      textAlign: 'center',
      // width: '2%',
    },
  },
  {
    title: t('project:project-reference'),
    field: 'projectReference',
    grouping: false,
    headerStyle: { width: '30%' },
  },
  {
    title: t('common:supplier'),
    field: 'sellingPartyName',
    headerStyle: { width: '15%' },
  },
  {
    title: t('common:customer'),
    field: 'buyingPartyName',
    headerStyle: { width: '15%' },
  },
  {
    title: t('common:num-parts', '# parts'),
    field: 'numberOfRows',
    grouping: false,
    cellStyle: { textAlign: 'center' },
    headerStyle: { textAlign: 'center', width: '8%' },
  },
  {
    title: t('common:status'),
    field: 'status',
    headerStyle: { width: '15%' },
    render: (rowData, type) => (
      <LocalizedTypography
        component="span"
        translationKey={`project:${
          type === 'group' ? (rowData as string) : rowData.status
        }`}
      />
    ),
  },
  {
    title: t('project:net-sales-price', 'net sales price'),
    field: 'salesPriceOfItems.value',
    render: (rowData) => {
      return MoneyString(rowData.salesPriceOfItems)
    },
    grouping: false,
    cellStyle: { textAlign: 'right', paddingRight: '52px' },
    headerStyle: {
      textAlign: 'right',
      // width: '15%',
    },
  },

  {
    title: t('common:date'),
    field: 'lastOperation',
    grouping: true,
    type: 'date',
    render: (row, type) => (
      <span>
        {t('common:formattedDate', {
          date: type === 'group' ? (row as string) : row.lastOperation,
        })}
      </span>
    ),
  },
  {
    title: t('common:created-by', 'created by'),
    field: 'creator.email',
    customFilterAndSearch: (term, rowData) => {
      return Boolean(
        matchSorter(
          [
            `${rowData.creator?.firstName}`,
            `${rowData.creator?.lastName}`,
            `${rowData.creator?.email}`,
          ],
          term
        ).length
      )
    },

    render: (row, type) => {
      let creator: CreatorDto

      if (type === 'group') {
        return row as string
      } else {
        creator = row.creator
      }

      return creator
        ? `${creator?.firstName || ''} ${creator?.lastName || ''}`.trim()
        : null
    },
  },
  {
    title: t('common:sales-representative', 'sales representative'),
    field: 'salesPerson.email',
    // defaultFilter: props.onlyMyProjects && user.email,
    customFilterAndSearch: (term, rowData) => {
      return Boolean(
        matchSorter(
          [
            `${rowData.creator?.firstName}`,
            `${rowData.creator?.lastName}`,
            `${rowData.creator?.email}`,
          ],
          term
        ).length
      )
    },

    render: (row, type) => {
      let salesPerson: SalesPersonDto

      if (type === 'group') {
        return row as string
      } else {
        salesPerson = row.salesPerson
      }

      return salesPerson
        ? `${salesPerson?.firstName || ''} ${
            salesPerson?.lastName || ''
          }`.trim()
        : null
    },
  },
]

export const ProjectsListPage = () => {
  const {
    fetchProjectListPaged,
    onlyMyProjects,
    setOnlyMyProjects,
    pageSize,
    setPageSize,
    connectToUpdates,
    disconnectFromUpdates,
    groupName,
    loading,
  } = useProjectList()

  const navigate = useNavigate()
  const theme = useTheme()
  const localization = useMaterialTableLocalization()

  const tableRef = useRef<{
    onQueryChange: () => void
    setState: (obj: Record<string, unknown>) => void
    dataManager: { changePageSize: (pageSize: number) => void }
  }>()

  useEffect(() => {
    tableRef.current.dataManager.changePageSize(pageSize)
    connectToUpdates(() => tableRef.current?.onQueryChange())

    return () => {
      disconnectFromUpdates()
    }
  }, [connectToUpdates, disconnectFromUpdates, pageSize])

  const dispatch = useAppDispatch()

  const handleDeleteSelectedProjects = async (
    _evt,
    data: ProjectSummaryDto | ProjectSummaryDto[]
  ) => {
    try {
      if (data instanceof Array) {
        dispatch(
          deleteProjects(
            data.map((x) => {
              const fix = x.status ? x.status.toUpperCase() : ''

              return {
                projectId: x.id,
                projectStatus: ProjectStateAsString[fix],
              }
            })
          )
        )
      } else {
        const fix = data.status ? data.status.toUpperCase() : ''

        dispatch(
          deleteProjects([
            {
              projectId: data.id,
              projectStatus: ProjectStateAsString[fix],
            },
          ])
        )
      }
    } catch (err) {
      ShowException('delete projects', err)
    }
  }

  const projectListOperation = useAppSelector(
    (state: RootAppState) => ({
      operation: state.projectList.operation,
      operationStatus: state.projectList.operationStatus,
      operationError: state.projectList.operationError,
    }),
    isEqual
  )

  useEffect(() => {
    if (projectListOperation.operation === 'delete') {
      switch (projectListOperation?.operationStatus) {
        case 'pending': {
          tableRef.current.setState({ isLoading: true })
          break
        }
        case 'fulfilled': {
          tableRef.current.onQueryChange()
          break
        }
        case 'rejected': {
          ShowException('delete projects', projectListOperation.operationError)
          tableRef.current.onQueryChange()
          break
        }
      }
    }
  }, [projectListOperation])

  const { t } = useTranslation()
  const _columns = useMemo(() => columns(t), [t])

  return (
    <>
      <MaterialTable
        localization={localization}
        tableRef={tableRef}
        title={
          <ProjectsPageHelpers
            handleOnlyMyProjects={(myProjects) => {
              setOnlyMyProjects(myProjects)
              tableRef.current.onQueryChange()
            }}
            onlyMyProjects={onlyMyProjects}
            handleReload={() => {
              tableRef.current.onQueryChange()
            }}
            reloading={loading['GetProjectsListPaged']}
          />
        }
        data={async (query: Query<ProjectSummaryDto>) => {
          const result = await fetchProjectListPaged({
            page: query.page,
            pageSize: query.pageSize,
            orderBy: (query.orderBy?.field.split('.')[0] ||
              'lastOperation') as keyof ProjectSummaryDto,
            orderDirection: query.orderDirection || 'desc',
            search: query.search,
          })

          return {
            data: result.details || [],
            page: result.page,
            totalCount: result.totalCount,
          } as QueryResult<ProjectSummaryDto>
        }}
        columns={_columns}
        onChangeRowsPerPage={setPageSize}
        options={{
          loadingType: 'linear',
          initialPage: 0,
          pageSize: 100,
          pageSizeOptions: [20, 50, 100, 250],
          emptyRowsWhenPaging: true,
          showFirstLastPageButtons: true,
          exportAllData: true,
          actionsColumnIndex: -1,
          paging: true,
          paginationType: 'normal',
          draggable: true,
          grouping: true,
          selection: true,
          showSelectAllCheckbox: true,
          headerStyle: {
            position: 'sticky',
            top: 0,
            fontSize: '0.775em',
            fontWeight: 'bold',
            backgroundColor: theme.palette.background.paper,
            zIndex: 2,
          },
          maxBodyHeight: 'calc(100vh - 332px)',
          showTitle: true,
        }}
        onRowClick={(e, rowData) => {
          navigate(`/app/project/${rowData.id}`)
        }}
        editable={{
          isDeletable: (rowData) => {
            const fix = rowData.status ? rowData.status.toUpperCase() : ''
            return fix !== ProjectStateAsString.ORDERED
          },
          isDeleteHidden: (rowData) => {
            const fix = rowData.status ? rowData.status.toUpperCase() : ''
            return fix === ProjectStateAsString.ORDERED
          },
          onRowDelete: (rowData) => {
            return new Promise((resolve) => {
              setTimeout(() => {
                const fix = rowData.status ? rowData.status.toUpperCase() : ''
                dispatch(
                  deleteProjects([
                    {
                      projectId: rowData.id,
                      projectStatus: ProjectStateAsString[fix],
                      confirmationDone: true,
                    },
                  ])
                )
                resolve(true)
              }, 100)
            })
          },
        }}
        actions={[
          {
            tooltip: 'Remove All Selected Projects',
            icon: () => <TrashSvg className={'MuiSvgIcon-root'} />,
            onClick: handleDeleteSelectedProjects,
          },
        ]}
        icons={{
          Delete: forwardRef((props, ref) => (
            <TrashSvg {...props} className={'MuiSvgIcon-root'} ref={ref} />
          )),
          Search: forwardRef((props, ref) => (
            <SearchSvg {...props} className={'MuiSvgIcon-root'} ref={ref} />
          )),
        }}
      />
      <ConnectionState expectedGroup={groupName} />
    </>
  )
}
